import { FadeAnimator, Lottie } from '@lemonade-hq/cdk';
import { ErrorPage, useAnalyticsContext, useI18nContext } from '@lemonade-hq/nuiza';
import { useCallback, useEffect, useState } from 'react';
import { PartnerName } from '../../../../definitions';
import { useCreatePriceOffer } from '../../queries';
import { Event } from '../../utils/analytics';
import { AnimateLogoPair } from './AnimateLogoPair';
import { PARTNER_NAME_TO_LOGO } from './consts';
import lemonadeFillUpAnimation from './lemonadeFillUp.json';
import { BrandPriceCompressionTitle } from './styles';
import type { BrandPriceComparisonProps } from './types';
import { Error } from './types';

export const BrandPriceComparison: React.FC<BrandPriceComparisonProps> = ({
  priceOfferId,
  priceOfferApiVersion,
  addons,
  partnerName,
  utmParams,
}) => {
  const [showTitle, setShowTitle] = useState(false);
  const [error, setError] = useState(false);
  const [isLoopComplete, setIsLoopComplete] = useState(false);
  const { data, mutate, isError, isLoading } = useCreatePriceOffer(priceOfferId, priceOfferApiVersion, utmParams);
  const { s } = useI18nContext();
  const { trackEvent } = useAnalyticsContext();
  useEffect(() => {
    trackEvent(Event.PcwLoadingPageViewed, partnerName != null ? { partner: partnerName } : undefined);
  }, [partnerName, trackEvent]);

  const handleDOMLoad = (): void => {
    if (isLoading) return;

    mutate({ addons });
    setShowTitle(true);
  };

  const handleOnLoopComplete = useCallback(() => {
    if (isError) {
      setError(true);
      return;
    }

    setIsLoopComplete(true);
  }, [isError]);

  if (isLoopComplete && typeof data?.url === 'string') {
    window.location.replace(data.url);
  }

  if (error) {
    trackEvent(Event.PcwLoadingPageError, { type: Error.CreatePriceOffer, partner: partnerName ?? '' });
    return <ErrorPage />;
  }

  if (partnerName != null) {
    const isPartnerSupported = Object.values(PartnerName).includes(partnerName);

    if (!isPartnerSupported) {
      trackEvent(Event.PcwLoadingPageError, { type: Error.PartnerNotSupported, partner: partnerName });
      return <ErrorPage />;
    }

    return (
      <AnimateLogoPair
        logoComponentOrUrlArray={PARTNER_NAME_TO_LOGO[partnerName]}
        onDOMLoaded={handleDOMLoad}
        onFinish={handleOnLoopComplete}
        title={s('brand_loading.price_compression.brand.title')}
      />
    );
  }

  return (
    <>
      <Lottie
        animationData={lemonadeFillUpAnimation}
        aria-label="Brand animation"
        autoplay
        onDOMLoaded={handleDOMLoad}
        onLoopComplete={handleOnLoopComplete}
        style={{ height: 108 }}
      />

      <FadeAnimator duration={1000} shown={showTitle}>
        <BrandPriceCompressionTitle aria-label="Brand title">
          {s('brand_loading.price_compression.title')}
        </BrandPriceCompressionTitle>
      </FadeAnimator>
    </>
  );
};
